import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkDetailPage from '../../../components/work/WorkDetailPage';
import {
  projectTemplatesApi,
  projectTemplateMembersApi,
  projectTemplateTasksApi,
  projectTemplateMilestonesApi,
  projectCategoriesApi,
} from '../../../utils/workApi';
import {
  buildProjectTemplateMemberGroupPayload,
  buildProjectTemplateMemberPayload,
  buildProjectTemplateMilestonePayload,
  buildProjectTemplateTaskPayload,
} from '../../../utils/workPayloadBuilders';
import {
  getProjectTemplateFromResponse,
  resolveProjectTemplateCategoryName,
} from '../../../utils/workResponseUtils';
import { TaskPriorityTag } from '../../../utils/workTaskUtils';
import { workActionsColumn } from '../../../utils/workTableHelpers';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { show422Error } from '../../../utils/api';
import {
  Button,
  Card,
  Tabs,
  Table,
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Col,
  Spin,
  Descriptions,
} from 'antd';
import { ProjectOutlined, PlusOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../../components/PersianDatePicker';
import WorkPriceInput from '../../../components/work/WorkPriceInput';
import { formatWorkPriceDisplay } from '../../../utils/workPriceUtils';
import { toJalali } from '../../../utils/dateUtils';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';

const { TextArea } = Input;
const { Option } = Select;
const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const TabToolbar = ({ children }) => (
  <div className="flex justify-end mb-4" style={{ fontFamily: 'CustomFont' }}>
    {children}
  </div>
);

const parseLabelIds = (raw) => {
  if (Array.isArray(raw)) return raw.map((x) => (typeof x === 'object' ? Number(x.id) : Number(x))).filter(Boolean);
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') return raw.split(',').map((x) => Number(x.trim())).filter(Boolean);
  return [Number(raw)].filter(Boolean);
};

const resolveTemplateTaskAssigneeIds = (task) => {
  if (!task) return [];
  if (Array.isArray(task.users) && task.users.length) {
    return task.users
      .map((u) => {
        if (u == null) return null;
        if (typeof u !== 'object') return Number(u);
        // رکورد pivot: { id, project_template_task_id, user_id }
        if (u.user_id != null) return Number(u.user_id);
        if (u.user?.id != null) return Number(u.user.id);
        // آبجکت کاربر کامل (دارای name)
        if (u.name != null && u.id != null) return Number(u.id);
        return null;
      })
      .filter((id) => id != null && !Number.isNaN(id));
  }
  if (Array.isArray(task.user_id)) {
    return task.user_id.map(Number).filter(Boolean);
  }
  if (typeof task.user_id === 'string' && task.user_id.trim()) {
    return task.user_id.split(',').map((x) => Number(x.trim())).filter(Boolean);
  }
  if (Array.isArray(task.project_template_task_users)) {
    return task.project_template_task_users
      .map((u) => Number(u.user_id ?? u.user?.id))
      .filter(Boolean);
  }
  return [];
};

const mergeEmployeeLists = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((e) => {
    if (!e) return;
    const id = Number(e.id);
    if (!id || Number.isNaN(id)) return;
    const prev = byId.get(id);
    const name = e.name || e.user?.name || prev?.name;
    byId.set(id, { ...prev, ...e, id, name: name || prev?.name });
  });
  return [...byId.values()];
};

const resolveAssigneeNames = (users, employeeMap = {}) => {
  const list = Array.isArray(users) ? users : [];
  const names = list
    .map((u) => {
      if (u == null) return null;
      if (typeof u !== 'object') {
        return employeeMap[Number(u)] || null;
      }
      if (u.name) return u.name;
      if (u.user?.name) return u.user.name;
      const uid = Number(u.user_id ?? u.user?.id);
      if (!uid || Number.isNaN(uid)) return null;
      return employeeMap[uid] || null;
    })
    .filter(Boolean);
  return names.length ? names.join('، ') : '—';
};

const ProjectTemplateView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/work/projects/templates', { state: buildGoBackState() });
  const templateId = Number(id);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [categoryLabel, setCategoryLabel] = useState('—');
  const [tpl, setTpl] = useState(null);
  const { trail, navigateWithTrail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/projects/templates',
    listLabel: 'قالب‌های پروژه',
    entityLabel: tpl?.project_name || (id ? `قالب #${id}` : 'قالب پروژه'),
  });
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);

  const [memberModal, setMemberModal] = useState(false);
  const [taskModal, setTaskModal] = useState(false);
  const [msModal, setMsModal] = useState(false);
  const [memberSaving, setMemberSaving] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [msSaving, setMsSaving] = useState(false);
  const [memberModalLoading, setMemberModalLoading] = useState(false);
  const [taskModalLoading, setTaskModalLoading] = useState(false);
  const [msModalLoading, setMsModalLoading] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);
  const [editMsId, setEditMsId] = useState(null);
  const [msStatusUpdatingId, setMsStatusUpdatingId] = useState(null);
  const [viewTaskOpen, setViewTaskOpen] = useState(false);
  const [viewTaskLoading, setViewTaskLoading] = useState(false);
  const [viewTask, setViewTask] = useState(null);
  const [viewTaskEmployeeMap, setViewTaskEmployeeMap] = useState({});

  const [memberOpts, setMemberOpts] = useState({ employees: [], departments: [] });
  const [taskMeta, setTaskMeta] = useState({ categories: [], employees: [], labels: [], milestones: [] });
  const [msMeta, setMsMeta] = useState({ currencies: [] });

  const [memberForm] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [msForm] = Form.useForm();

  const reload = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await projectTemplatesApi.getById(id);
      const t = getProjectTemplateFromResponse(res);
      setTpl(t);
      setMembers(t?.members || res?.members || []);
      setTasks(t?.tasks || res?.tasks || []);
      setMilestones(t?.milestones || res?.milestones || []);
      let categories = [];
      try {
        const catRes = await projectCategoriesApi.getCreateData();
        categories = catRes?.categories || [];
      } catch {
        categories = [];
      }
      setCategoryLabel(resolveProjectTemplateCategoryName(t, categories));
    } catch {
      message.error('خطا در بارگذاری');
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [id]);

  const openMemberModal = async () => {
    setMemberModalLoading(true);
    try {
      const res = await projectTemplateMembersApi.getCreateData(id);
      setMemberOpts({
        employees: res?.employees || [],
        departments: res?.departments || [],
      });
      memberForm.resetFields();
      setMemberModal(true);
    } catch {
      message.error('خطا در بارگذاری فرم عضو');
    } finally {
      setMemberModalLoading(false);
    }
  };

  const saveMember = async () => {
    const user_id = memberForm.getFieldValue('user_id');
    if (!user_id?.length) {
      message.warning('حداقل یک کارمند انتخاب کنید');
      return;
    }
    setMemberSaving(true);
    try {
      await projectTemplateMembersApi.create(
        buildProjectTemplateMemberPayload(templateId, user_id),
      );
      message.success('اعضا اضافه شدند');
      setMemberModal(false);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, memberForm);
    } finally {
      setMemberSaving(false);
    }
  };

  const saveMemberGroup = async () => {
    const groupId = memberForm.getFieldValue('group_id');
    if (!groupId) {
      message.warning('تیم را انتخاب کنید');
      return;
    }
    setMemberSaving(true);
    try {
      await projectTemplateMembersApi.saveGroup(
        buildProjectTemplateMemberGroupPayload(templateId, [groupId]),
      );
      message.success('اعضای تیم اضافه شدند');
      setMemberModal(false);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, memberForm);
    } finally {
      setMemberSaving(false);
    }
  };

  const openTaskModal = async (taskId = null) => {
    setEditTaskId(taskId);
    setTaskModalLoading(true);
    try {
      if (taskId) {
        // فقط از edit وظیفه: employees برای لیست نام‌ها، users برای مقدار انتخاب‌شده
        const ed = await projectTemplateTasksApi.getEditData(taskId);
        const t = ed?.task || ed;
        const employees = mergeEmployeeLists(ed?.employees || []);
        const assigneeIds = resolveTemplateTaskAssigneeIds(t);

        setTaskMeta({
          categories: ed?.categories || [],
          employees,
          labels: ed?.labels || [],
          milestones: ed?.milestones || [],
        });
        setTaskModal(true);
        queueMicrotask(() => {
          taskForm.setFieldsValue({
            heading: t.heading,
            description: t.description,
            category_id:
              t.project_template_task_category_id != null
                ? Number(t.project_template_task_category_id)
                : t.category_id != null
                  ? Number(t.category_id)
                  : undefined,
            user_id: assigneeIds.map(Number),
            priority: t.priority || 'medium',
            task_labels: parseLabelIds(t.task_labels),
            milestone_id: t.milestone_id != null ? Number(t.milestone_id) : undefined,
          });
        });
      } else {
        const res = await projectTemplateTasksApi.getCreateData(id);
        setTaskMeta({
          categories: res?.categories || [],
          employees: mergeEmployeeLists(res?.employees || []),
          labels: res?.labels || [],
          milestones: res?.milestones || [],
        });
        taskForm.resetFields();
        setTaskModal(true);
        queueMicrotask(() => {
          taskForm.setFieldsValue({ priority: 'medium' });
        });
      }
    } catch {
      message.error('خطا در بارگذاری فرم وظیفه');
    } finally {
      setTaskModalLoading(false);
    }
  };

  const openViewTask = async (taskId) => {
    setViewTaskOpen(true);
    setViewTaskLoading(true);
    setViewTask(null);
    setViewTaskEmployeeMap({});
    try {
      // جزئیات از show؛ نام مسئولین از employees در edit (همان منبع ویرایش)
      const [res, ed] = await Promise.all([
        projectTemplateTasksApi.getById(taskId),
        projectTemplateTasksApi.getEditData(taskId).catch(() => null),
      ]);
      const task = res?.task || res?.data?.task || res;
      const map = {};
      mergeEmployeeLists(ed?.employees || []).forEach((e) => {
        if (e?.id && e?.name) map[Number(e.id)] = e.name;
      });
      // اگر در users آبجکت user پر بود، اولویت با همان
      (Array.isArray(task?.users) ? task.users : []).forEach((u) => {
        const uid = Number(u?.user_id ?? u?.user?.id);
        const name = u?.user?.name || u?.name;
        if (uid && name) map[uid] = name;
      });
      setViewTaskEmployeeMap(map);
      setViewTask(task);
    } catch {
      message.error('خطا در دریافت وظیفه');
      setViewTaskOpen(false);
    } finally {
      setViewTaskLoading(false);
    }
  };

  const saveTask = async () => {
    try {
      const v = await taskForm.validateFields();
      setTaskSaving(true);
      const body = buildProjectTemplateTaskPayload(v, templateId);
      if (editTaskId) {
        await projectTemplateTasksApi.update(editTaskId, body);
      } else {
        await projectTemplateTasksApi.create(body);
      }
      message.success('وظیفه قالب ذخیره شد');
      setTaskModal(false);
      setEditTaskId(null);
      await reload({ silent: true });
    } catch (e) {
      if (e?.errorFields) return;
      show422Error(e, taskForm);
    } finally {
      setTaskSaving(false);
    }
  };

  const openMsModal = async (milestoneId = null) => {
    setMsModalLoading(true);
    try {
      const res = await projectTemplateMilestonesApi.getCreateData(id);
      setMsMeta({ currencies: res?.currencies || [] });
      if (milestoneId) {
        const ed = await projectTemplateMilestonesApi.getEditData(milestoneId);
        const m = ed?.milestone || ed;
        if (ed?.currencies?.length) {
          setMsMeta({ currencies: ed.currencies });
        }
        msForm.setFieldsValue({
          milestone_title: m.milestone_title,
          summary: m.summary,
          cost: m.cost,
          status: m.status || 'incomplete',
          currency_id: m.currency_id != null ? Number(m.currency_id) : undefined,
          start_date: m.start_date ? toJalali(m.start_date) : undefined,
          end_date: m.end_date ? toJalali(m.end_date) : undefined,
        });
        setEditMsId(milestoneId);
      } else {
        msForm.resetFields();
        msForm.setFieldsValue({ status: 'incomplete' });
        setEditMsId(null);
      }
      setMsModal(true);
    } catch {
      message.error('خطا در بارگذاری فرم مرحله');
    } finally {
      setMsModalLoading(false);
    }
  };

  const saveMilestone = async () => {
    try {
      const v = await msForm.validateFields();
      setMsSaving(true);
      const body = buildProjectTemplateMilestonePayload(v, templateId);
      if (editMsId) {
        await projectTemplateMilestonesApi.update(editMsId, body);
      } else {
        await projectTemplateMilestonesApi.create(body);
      }
      message.success('مرحله ذخیره شد');
      setMsModal(false);
      setEditMsId(null);
      await reload({ silent: true });
    } catch (e) {
      if (e?.errorFields) return;
      show422Error(e, msForm);
    } finally {
      setMsSaving(false);
    }
  };

  const addBtn = (text, onClick, loadingBtn = false) => (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      className={PANEL_PRIMARY_BTN_CLASS}
      onClick={onClick}
      loading={loadingBtn}
      style={{ fontFamily: 'CustomFont' }}
    >
      {text}
    </Button>
  );

  return (
    <WorkDetailPage
      title={tpl?.project_name || 'جزئیات الگو'}
      subtitle="الگوی پروژه"
      icon={ProjectOutlined}
      iconClassName="bg-sky-50 dark:bg-sky-900/20"
      iconColorClass="text-sky-600 dark:text-sky-400"
      backPath="/work/projects/templates"
      onBack={goBackToList}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
      loading={loading}
      headerExtra={
        <Button
          type="primary"
          className={PANEL_PRIMARY_BTN_CLASS}
          onClick={() =>
            navigateWithTrail(
              `/work/projects/templates/${id}/edit`,
              tpl?.project_name ? `ویرایش ${tpl.project_name}` : `ویرایش قالب #${id}`,
              { returnTo: returnToRef.current || '/work/projects/templates', ...buildGoBackState() },
            )
          }
          style={{ fontFamily: 'CustomFont' }}
        >
          ویرایش الگو
        </Button>
      }
    >
      <Card className="shadow-sm border-0 mb-4 work-panel-form" style={{ fontFamily: 'CustomFont' }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12}>
            <strong style={{ fontFamily: 'CustomFont' }}>نام: </strong>
            <span style={{ fontFamily: 'CustomFont' }}>{tpl?.project_name || '—'}</span>
          </Col>
          <Col xs={24} sm={12}>
            <strong style={{ fontFamily: 'CustomFont' }}>دسته: </strong>
            <span style={{ fontFamily: 'CustomFont' }}>{categoryLabel}</span>
          </Col>
          <Col xs={24}>
            <strong style={{ fontFamily: 'CustomFont' }}>خلاصه: </strong>
            <span style={{ fontFamily: 'CustomFont' }} className="whitespace-pre-wrap">
              {tpl?.project_summary || '—'}
            </span>
          </Col>
        </Row>
      </Card>

      <Card className="shadow-sm border-0 work-panel-form" style={{ fontFamily: 'CustomFont' }}>
        <Spin spinning={refreshing}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'members',
              label: label('اعضا'),
              children: (
                <>
                  <TabToolbar>{addBtn('افزودن عضو', openMemberModal, memberModalLoading)}</TabToolbar>
                  <Table
                    rowKey="id"
                    dataSource={members}
                    pagination={false}
                    scroll={{ x: true }}
                    columns={[
                      {
                        title: label('نام'),
                        render: (_, r) => (
                          <span style={{ fontFamily: 'CustomFont' }}>{r.user?.name || '—'}</span>
                        ),
                      },
                      workActionsColumn((_, row) => (
                        <Popconfirm
                          title={<span style={{ fontFamily: 'CustomFont' }}>حذف عضو؟</span>}
                          onConfirm={async () => {
                            await projectTemplateMembersApi.delete(row.id);
                            await reload({ silent: true });
                          }}
                        >
                          <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )),
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'ms',
              label: label('مراحل'),
              children: (
                <>
                  <TabToolbar>{addBtn('افزودن مرحله', () => openMsModal(), msModalLoading)}</TabToolbar>
                  <Table
                    rowKey="id"
                    dataSource={milestones}
                    pagination={false}
                    scroll={{ x: true }}
                    columns={[
                      {
                        title: label('عنوان'),
                        dataIndex: 'milestone_title',
                        render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v}</span>,
                      },
                      {
                        title: label('هزینه مرحله'),
                        dataIndex: 'cost',
                        width: 140,
                        render: (v) => (
                          <span style={{ fontFamily: 'CustomFont' }}>
                            {formatWorkPriceDisplay(v)}
                          </span>
                        ),
                      },
                      {
                        title: label('وضعیت'),
                        width: 140,
                        render: (_, row) => (
                          <Select
                            size="small"
                            value={row.status}
                            style={{ width: '100%', fontFamily: 'CustomFont' }}
                            loading={msStatusUpdatingId === row.id}
                            onChange={async (status) => {
                              setMsStatusUpdatingId(row.id);
                              try {
                                await projectTemplateMilestonesApi.updateStatus(row.id, status);
                                await reload({ silent: true });
                              } finally {
                                setMsStatusUpdatingId(null);
                              }
                            }}
                            options={[
                              { value: 'incomplete', label: 'ناتمام' },
                              { value: 'complete', label: 'تمام' },
                            ]}
                          />
                        ),
                      },
                      workActionsColumn((_, row) => (
                        <>
                          <Button type="text" icon={<EditOutlined />} onClick={() => openMsModal(row.id)} />
                          <Popconfirm
                            title={<span style={{ fontFamily: 'CustomFont' }}>حذف؟</span>}
                            onConfirm={async () => {
                              await projectTemplateMilestonesApi.delete(row.id);
                              await reload({ silent: true });
                            }}
                          >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </>
                      )),
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'tasks',
              label: label('وظایف'),
              children: (
                <>
                  <TabToolbar>{addBtn('افزودن وظیفه', () => openTaskModal(), taskModalLoading)}</TabToolbar>
                  <Table
                    rowKey="id"
                    dataSource={tasks}
                    pagination={false}
                    scroll={{ x: true }}
                    columns={[
                      {
                        title: label('عنوان'),
                        dataIndex: 'heading',
                        render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v}</span>,
                      },
                      {
                        title: label('اولویت'),
                        dataIndex: 'priority',
                        width: 110,
                        render: (p) => <TaskPriorityTag priority={p} />,
                      },
                      workActionsColumn((_, row) => (
                        <>
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => openViewTask(row.id)}
                          />
                          <Button type="text" icon={<EditOutlined />} onClick={() => openTaskModal(row.id)} />
                          <Popconfirm
                            title={<span style={{ fontFamily: 'CustomFont' }}>حذف؟</span>}
                            onConfirm={async () => {
                              await projectTemplateTasksApi.delete(row.id);
                              await reload({ silent: true });
                            }}
                          >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </>
                      )),
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
        </Spin>
      </Card>

      <Modal
        open={memberModal}
        title={label('افزودن عضو')}
        onCancel={() => setMemberModal(false)}
        onOk={saveMember}
        confirmLoading={memberSaving}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setMemberModal(false)} style={{ fontFamily: 'CustomFont' }}>
            لغو
          </Button>,
          <Button
            key="group"
            loading={memberSaving}
            onClick={saveMemberGroup}
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن تیم
          </Button>,
          <Button
            key="ok"
            type="primary"
            className={PANEL_PRIMARY_BTN_CLASS}
            loading={memberSaving}
            onClick={saveMember}
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن کارمند
          </Button>,
        ]}
      >
        <Form form={memberForm} layout="vertical" className="work-panel-form">
          <Form.Item name="user_id" label={label('کارمندان')}>
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              options={(memberOpts.employees || []).map((e) => ({ value: e.id, label: e.name }))}
            />
          </Form.Item>
          <Form.Item name="group_id" label={label('تیم')}>
            <Select
              allowClear
              optionFilterProp="label"
              placeholder="انتخاب تیم"
              options={(memberOpts.departments || []).map((d) => ({
                value: d.id,
                label: d.team_name || d.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={taskModal}
        title={label(editTaskId ? 'ویرایش وظیفه' : 'افزودن وظیفه')}
        onCancel={() => {
          setTaskModal(false);
          setEditTaskId(null);
        }}
        onOk={saveTask}
        confirmLoading={taskSaving}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
        width={720}
      >
        <Form form={taskForm} layout="vertical" className="work-panel-form">
          <Form.Item name="heading" label={label('عنوان')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={label('توضیحات')}>
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="category_id" label={label('دسته')} rules={[{ required: true }]}>
                <Select allowClear showSearch optionFilterProp="label">
                  {taskMeta.categories.map((c) => (
                    <Option key={c.id} value={c.id} label={c.category_name}>
                      {c.category_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="priority" label={label('اولویت')}>
                <Select>
                  <Option value="high">بالا</Option>
                  <Option value="medium">متوسط</Option>
                  <Option value="low">پایین</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="user_id" label={label('مسئولین')}>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={taskMeta.employees.map((e) => ({
                    value: Number(e.id),
                    label: e.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="task_labels" label={label('تگ‌ها')}>
                <Select mode="multiple" allowClear>
                  {taskMeta.labels.map((l) => (
                    <Option key={l.id} value={l.id}>
                      {l.label_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="milestone_id" label={label('مرحله')}>
                <Select allowClear>
                  {taskMeta.milestones.map((m) => (
                    <Option key={m.id} value={m.id}>
                      {m.milestone_title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        open={msModal}
        title={label(editMsId ? 'ویرایش مرحله' : 'افزودن مرحله')}
        onCancel={() => {
          setMsModal(false);
          setEditMsId(null);
        }}
        onOk={saveMilestone}
        confirmLoading={msSaving}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
        width={640}
      >
        <Form form={msForm} layout="vertical" className="work-panel-form">
          <Form.Item name="milestone_title" label={label('عنوان')} rules={[{ required: true, message: 'عنوان الزامی است' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="summary" label={label('خلاصه')} rules={[{ required: true, message: 'خلاصه الزامی است' }]}>
            <TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="cost" label={label('هزینه')}>
                <WorkPriceInput />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="currency_id" label={label('ارز')}>
                <Select allowClear>
                  {msMeta.currencies.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.currency_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="start_date" label={label('تاریخ شروع')}>
                <PersianDatePicker className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="end_date" label={label('تاریخ پایان')}>
                <PersianDatePicker className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="status" label={label('وضعیت')}>
                <Select>
                  <Option value="incomplete">ناتمام</Option>
                  <Option value="complete">تمام</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        open={viewTaskOpen}
        title={label('مشاهده وظیفه')}
        onCancel={() => {
          setViewTaskOpen(false);
          setViewTask(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setViewTaskOpen(false);
              setViewTask(null);
            }}
            style={{ fontFamily: 'CustomFont' }}
          >
            بستن
          </Button>,
          viewTask?.id ? (
            <Button
              key="edit"
              type="primary"
              className={PANEL_PRIMARY_BTN_CLASS}
              icon={<EditOutlined />}
              onClick={() => {
                setViewTaskOpen(false);
                openTaskModal(viewTask.id);
              }}
              style={{ fontFamily: 'CustomFont' }}
            >
              ویرایش
            </Button>
          ) : null,
        ]}
        width={640}
        destroyOnClose
      >
        {viewTaskLoading ? (
          <div className="py-8 text-center"><Spin /></div>
        ) : (
          <Descriptions
            bordered
            column={1}
            size="small"
            labelStyle={{ fontFamily: 'CustomFont' }}
            contentStyle={{ fontFamily: 'CustomFont' }}
          >
            <Descriptions.Item label={label('عنوان')}>{viewTask?.heading || '—'}</Descriptions.Item>
            <Descriptions.Item label={label('اولویت')}>
              <TaskPriorityTag priority={viewTask?.priority} />
            </Descriptions.Item>
            <Descriptions.Item label={label('دسته')}>
              {viewTask?.category?.category_name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={label('مرحله')}>
              {viewTask?.milestone?.milestone_title || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={label('مسئولین')}>
              {resolveAssigneeNames(viewTask?.users, viewTaskEmployeeMap)}
            </Descriptions.Item>
            <Descriptions.Item label={label('توضیحات')}>
              <span className="whitespace-pre-wrap">{viewTask?.description || '—'}</span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </WorkDetailPage>
  );
};

export default ProjectTemplateView;
